/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/_shadcn-ui/popover';
import MultiselectAutoCompleteList from './multiselect-autocomplete-list';
import MultiselectAutoCompleteTrigger from './multiselect-autocomplete-trigger';
import MultiselectAutoCompleteSearch from './multiselect-autocomplete-search';

export type GenericOption<T> = T;

export interface MultiselectAutoCompleteProps<T extends Record<string, any>> {
  id?: string;
  options: GenericOption<T>[];
  value: GenericOption<T>[];
  defaultValue?: GenericOption<T>[];
  onChange: (value: GenericOption<T>[]) => void;
  disabled?: boolean;

  // Optional UI configs
  isDisplaySelectedItem?: boolean;
  limit?: number;
  placeholder?: string;
  searchPlaceholder?: string;
  isLoading?: boolean;

  // Custom keys
  labelKey: keyof T;
  valueKey: keyof T;

  // Renderers
  onSearch?: (value: string) => void;
  renderOption?: (option: GenericOption<T>) => React.ReactNode;
  renderSelectedItem?: (option: GenericOption<T>, onRemove: () => void) => React.ReactNode;
  onClearAll?: () => void;
}

const MultiselectAutoComplete = <T extends Record<string, any>>({
  id,
  options = [],
  value = [],
  defaultValue = [],
  disabled = false,

  // Optional UI configs
  isDisplaySelectedItem = true,
  isLoading = false,
  limit = 2,
  placeholder = 'Select options',
  searchPlaceholder = 'Search...',

  // Custom Keys
  labelKey,
  valueKey,

  // Renderers
  onChange,
  onSearch,
  onClearAll,
  renderOption,
  renderSelectedItem,
}: MultiselectAutoCompleteProps<T>) => {
  // HOOKS
  const [isOpen, setIsOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [internalSearch, setInternalSearch] = useState('');
  const [internalValue, setInternalValue] = useState<GenericOption<T>[]>(defaultValue);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // HELPERS
  // get label logic, get the label of the option by the label key
  const getLabel = useCallback((option: T) => String(option?.[labelKey] ?? ''), [labelKey]);

  // get value logic, get the value of the option by the value key
  const getValue = useCallback(
    (option: T): string | number => {
      const value = option?.[valueKey];
      return typeof value === 'string' || typeof value === 'number' ? value : String(value);
    },
    [valueKey]
  );

  // filtered options logic, filter the options based on the search query
  const filteredOptions = useMemo(
    () => options.filter((opt) => getLabel(opt).toLowerCase().includes(internalSearch.toLowerCase())),
    [options, internalSearch, getLabel]
  );

  // is selected logic, check if the option is selected by comparing the value of the option with the value of the internal value
  const isSelected = useCallback(
    (option: T) => Boolean(internalValue.some((selected) => getValue(selected) === getValue(option))),
    [internalValue, getValue]
  );

  // EFFECTS
  useEffect(() => setInternalValue(value), [value]);

  // Cleanup timeout when popover closes or component unmounts
  useEffect(() => {
    if (!isOpen) {
      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      // Set new timeout to clear search
      timeoutRef.current = setTimeout(() => {
        setInternalSearch('');
        timeoutRef.current = null;
      }, 100);
    }

    // Cleanup function to clear timeout on unmount or when isOpen changes
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [isOpen]);

  // HANDLERS
  const handleToggle = useCallback(
    (option: T, checked: boolean) => {
      // toggle logic, simply pass the option and checked state to the handler
      const newValue = checked
        ? [...internalValue, option]
        : internalValue.filter((v) => getValue(v) !== getValue(option));

      // update the internal value and the external value
      setInternalValue(newValue);
      onChange(newValue);

      // if the number of selected options is less than the limit, close the dropdown
      if (newValue.length <= limit) setExpanded(false);
    },
    [internalValue, onChange, getValue, limit]
  );

  // remove logic, pass the option to the handler
  const handleRemove = useCallback((option: T) => handleToggle(option, false), [handleToggle]);

  // open/close dropdown logic, pass the open state to the handler
  const handleOpenChange = useCallback((open: boolean) => {
    setIsOpen(open);
  }, []);

  // clear all logic, pass the event to the handler
  const handleClearAll = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setInternalValue([]);
      onChange([]);
      setExpanded(false);
      onClearAll?.();
    },
    [onChange, onClearAll]
  );

  // search logic, pass the value to the handler
  const handleInternalSearch = useCallback(
    (value: string) => {
      setInternalSearch(value);
      onSearch?.(value);
    },
    [onSearch]
  );

  // expand logic, pass the value to the handler
  const handleExpand = useCallback((value: boolean) => {
    setExpanded(value);
  }, []);

  // RENDER
  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild className="w-full" disabled={disabled}>
        <MultiselectAutoCompleteTrigger
          isOpenList={isOpen}
          isDisplaySelectedItem={isDisplaySelectedItem}
          selected={internalValue}
          placeholder={placeholder}
          disabled={disabled}
          expanded={expanded}
          limit={limit}
          renderSelectedItem={renderSelectedItem}
          getLabel={getLabel}
          onExpand={handleExpand}
          onRemove={handleRemove}
          onClearAll={handleClearAll}
          onOpenChange={handleOpenChange}
        />
      </PopoverTrigger>

      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0 overflow-hidden rounded-[8px] border border-[#D9D9D9]">
        <div className="flex flex-col">
          <div className="p-[8px]">
            <MultiselectAutoCompleteSearch
              id={id}
              searchPlaceholder={searchPlaceholder}
              search={internalSearch}
              onSearch={handleInternalSearch}
            />
          </div>
          <MultiselectAutoCompleteList<T>
            isLoading={isLoading}
            options={filteredOptions}
            isSelected={isSelected}
            onToggle={handleToggle}
            getLabel={getLabel}
            renderOption={renderOption}
          />
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default MultiselectAutoComplete;
